'use client'

import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { FormEvent, useEffect, useMemo, useState } from 'react'

type SearchOption = {
  label: 'Clinic' | 'Prescriber' | 'Condition' | 'Speciality'
  searchBy: 'clinic name' | 'prescriber' | 'condition' | 'speciality'
}

const SEARCH_OPTIONS: SearchOption[] = [
  { label: 'Clinic', searchBy: 'clinic name' },
  { label: 'Prescriber', searchBy: 'prescriber' },
  { label: 'Condition', searchBy: 'condition' },
  { label: 'Speciality', searchBy: 'speciality' },
]

const Searchbar = () => {
  const router = useRouter()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLabel, setSelectedLabel] = useState<SearchOption['label']>('Clinic')

  const selectedOption = useMemo(
    () => SEARCH_OPTIONS.find((option) => option.label === selectedLabel) ?? SEARCH_OPTIONS[0],
    [selectedLabel]
  )

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!searchTerm.trim()) {
      return
    }

    if (selectedOption.searchBy === 'clinic name') {
      router.push(`clinic/cliniclisting/?search=${encodeURIComponent(searchTerm)}&searchBy=${selectedOption.searchBy}`)
      return
    }

    router.push(
      `clinic/prescriberslisting/?search=${encodeURIComponent(searchTerm)}&searchBy=${selectedOption.searchBy}`
    )
  }

  useEffect(() => {
    router.prefetch('/clinic/cliniclisting')
    router.prefetch('/clinic/prescriberslisting')
  }, [router])

  return (
    <form className='relative z-10 flex w-full justify-center' onSubmit={handleFormSubmit}>
      <div className='relative w-full  '>
        <div className='rounded-md bg-[#F7F9FC]  px-5 pb-5 pt-12 shadow-[0px_20px_45px_-20px_rgba(43,54,116,0.35)]'>
          <div className='flex items-center gap-3 rounded-full bg-white px-5 py-3 shadow-[0px_20px_45px_-20px_rgba(43,54,116,0.35)]'>
            <div className='absolute max-w-[900px] left-1/2 top-0 z-20 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-between rounded-md bg-white px-8 py-6 shadow-black/2 shadow-[0_0_15px_rgba(0,0,0,0.3)]'>
              {SEARCH_OPTIONS.map((option) => {
                const isActive = option.label === selectedOption.label
                return (
                  <button
                    key={option.label}
                    type='button'
                    aria-pressed={isActive}
                    className={`relative flex-1 px-2 sm:px-4 text-center text-sm  font-medium transition-colors duration-200 md:text-base ${
                      option.label !== 'Clinic' ? 'border-l border-[#E1E5F2]' : ''
                    } ${isActive ? 'text-[#F59405]' : 'text-[#0D263C]'}`}
                    onClick={() => setSelectedLabel(option.label)}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            <Image src='/icons/search.svg' alt='search' height={20} width={20} className='h-5 w-5 opacity-70' />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              type='text'
              placeholder={`Search by ${selectedOption.label.toLowerCase()}`}
              className='flex-1 bg-transparent text-sm text-[#0D263C] outline-none placeholder:text-[#A1B0C0] md:text-base'
            />
            <button
              type='submit'
              className='flex h-10 w-10 items-center justify-center rounded-full bg-[#F59405] text-white transition hover:bg-[#d78100]'
              aria-label='Submit search'
            >
              <Image src='/icons/search.svg' alt='submit search' height={18} width={18} className='h-[18px] w-[18px]' />
            </button>
          </div>
        </div>
      </div>
    </form>
  )
}

export default Searchbar
